import {
  getPaginationResponse,
  getSkipTakeFromPagination,
  type PaginationRequest,
  type PaginationResponse,
} from '#packages/data-collection/utils/pagination';
import { isNil } from '#packages/data-collection/libs/lodash';
import {
  AuditSchema,
  type NewAudit,
  type AuditId,
  type Audit,
  AuditNotFoundError,
} from '#packages/data-collection/models/audit';
import { type KeyValueRepo } from '#packages/data-collection/repositories/kv-repo';
import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import { type Clock } from '#packages/data-collection/services/clock';
import { IdGeneratorService } from '#packages/data-collection/services/id-generator';

import { LocalForageDbService } from '#apps/client-web/app/services/local-forage-db';
import { LocalForageKeyValueRepo } from '#apps/client-web/app/repositories/local-forage-kv-repo';
import { createModuleLogger } from '#apps/client-web/app/services/logger';
import { clock } from '#apps/client-web/app/services/clock';

export type CollectionName = 'active' | 'archived' | 'deleted';

type AuditEntity = ReturnType<typeof AuditSchema.encode>;

type AuditServiceOpts = {
  repo: KeyValueRepo<CollectionName, Array<AuditEntity>>;
  clock: Clock;
  idGenerator: IdGeneratorService;
} & LoggingContext;

export class AuditService {
  protected readonly opts: AuditServiceOpts;

  constructor(opts: AuditServiceOpts) {
    this.opts = opts;
  }

  protected generateId({ items }: { items: Array<Audit> }): AuditId {
    const { idGenerator } = this.opts;
    let itemId: AuditId;

    do {
      itemId = idGenerator.generate() as AuditId;
      const item = items.find((_) => _.id === itemId);
      if (isNil(item)) {
        break;
      }
    } while (true);

    return itemId;
  }

  protected async load({
    collectionName,
  }: {
    collectionName: CollectionName;
  }): Promise<Array<Audit>> {
    const storedItems = await this.opts.repo.get(collectionName);
    return (storedItems ?? []).map((item) => AuditSchema.decode(item));
  }

  protected save = async ({
    collectionName,
    items,
  }: {
    collectionName: CollectionName;
    items: Array<Audit>;
  }): Promise<void> => {
    await this.opts.repo.set(
      collectionName,
      items.map((item) => AuditSchema.encode(item)),
    );
  };

  async create({ newAudit }: { newAudit: NewAudit }): Promise<Audit> {
    const { clock, logger } = this.opts;

    logger?.debug({ newAudit }, 'Creating new audit');

    const items = await this.load({ collectionName: 'active' });

    const now = clock.now();

    // encode new audit
    const newEntity = AuditSchema.encode({
      id: this.generateId({ items }),

      auditDate: now,
      createdAt: now,
      updatedAt: now,

      ...newAudit,
    });
    logger?.debug({ newEntity }, 'Saving new entity to repo');

    // prepend to items
    const audit = AuditSchema.decode(newEntity);
    items.splice(0, 0, audit);

    // save to repo
    await this.save({ collectionName: 'active', items });

    return audit;
  }

  async loadAll({
    collectionName,
    pagination,
  }: {
    collectionName: CollectionName;
    pagination?: PaginationRequest;
  }): Promise<[Array<Audit>, { pagination: PaginationResponse }]> {
    const { logger } = this.opts;

    logger?.debug({ collectionName, pagination }, 'Loading all audits');

    const items = await this.load({ collectionName });

    // apply pagination
    const { skip, take } = getSkipTakeFromPagination(
      pagination ?? { pageNumber: 1, pageSize: Number.MAX_SAFE_INTEGER },
    );
    const paginatedItems = items.slice(skip, skip + take);
    const paginationResponse = getPaginationResponse({
      totalItems: items.length,
      pageSize: take,
    });

    return [paginatedItems, { pagination: paginationResponse }];
  }

  async loadById({
    collectionName,
    auditId,
  }: {
    collectionName: CollectionName;
    auditId: AuditId;
  }): Promise<Audit> {
    const { logger } = this.opts;

    logger?.debug({ collectionName, auditId }, 'Loading audit by ID');

    const items = await this.load({ collectionName });
    const existingEntity = items.find((item) => item.id === auditId);
    if (isNil(existingEntity)) {
      throw new AuditNotFoundError(auditId);
    }

    return existingEntity;
  }

  async update({
    collectionName,
    updatedAudit,
  }: {
    collectionName: CollectionName;
    updatedAudit: Audit;
  }): Promise<Audit> {
    const { clock, logger } = this.opts;

    logger?.debug({ collectionName, updatedAudit }, 'Updating audit');

    const items = await this.load({ collectionName });
    const itemIndex = items.findIndex((item) => item.id === updatedAudit.id);

    // check existence (cannot update non-existing)
    if (itemIndex < 0) {
      throw new AuditNotFoundError(updatedAudit.id);
    }

    // remove existing entity
    items.splice(itemIndex, 1);

    // set updatedAt
    updatedAudit.updatedAt = clock.now();

    // prepend updated audit
    items.splice(0, 0, updatedAudit);

    logger?.debug({ updatedAudit }, 'Saving updated entity to repo');
    await this.save({ collectionName, items });

    return updatedAudit;
  }

  async delete({
    collectionName,
    auditId,
  }: {
    collectionName: CollectionName;
    auditId: AuditId;
  }): Promise<Audit> {
    const { logger, clock } = this.opts;

    logger?.debug({ collectionName, auditId }, 'Deleting audit');

    const items = await this.load({ collectionName });
    const itemIndex = items.findIndex((item) => item.id === auditId);

    // check existence
    if (itemIndex < 0) {
      throw new AuditNotFoundError(auditId);
    }

    // remove existing entity
    const item = items[itemIndex];
    item.updatedAt = clock.now();

    items.splice(itemIndex, 1);

    logger?.debug({ auditId }, 'Saving updated items to repo after deletion');
    await this.save({ collectionName, items });

    if (collectionName !== 'deleted') {
      logger?.debug({ auditId }, 'Saving deleted audit to deleted collection');

      // save to deleted collection
      const deletedItems = await this.load({ collectionName: 'deleted' });
      deletedItems.splice(0, 0, item);
      await this.save({ collectionName: 'deleted', items: deletedItems });
    }

    return item;
  }
}

const logger = createModuleLogger('audit-service');

export const auditDatabase = LocalForageDbService.createInstance('audits');

export const auditRepo = new LocalForageKeyValueRepo<Array<AuditEntity>>({
  db: auditDatabase,
  logger,
});

const auditIdGenerator = new IdGeneratorService();

export const auditService = new AuditService({
  clock,
  repo: auditRepo,
  idGenerator: auditIdGenerator,
  logger,
});
